import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@bookaburra/ui';
import { Section, SectionEyebrow } from '../components/Section.js';
import { usePageTitle } from '../components/usePageTitle.js';
import { signupUrl } from '../lib/urls.js';

export const Route = createFileRoute('/pricing')({
  component: PricingPage,
});

// Pricing is decided, but billing infrastructure (company entity, GST,
// invoice generation, Stripe/etc.) isn't set up yet. Flip this flag to
// `true` once billing is wired and the rest of the page renders intact.
// Until then we render only the Free tier + a "coming soon" note for
// the paid plans, hide the comparison table, and trim the FAQ to items
// that still apply.
const PAID_PLANS_LIVE = true;

type Bullet = string | { label: string; href: string };

type Tier = {
  name: string;
  price: string;
  cadence: string;
  headline: string;
  bullets: Bullet[];
  notInTier: string[];
  cta: { label: string; href: string };
  featured: boolean;
};

const tiers: Tier[] = [
  {
    name: 'Free',
    price: 'Free',
    cadence: '',
    headline: 'For solo operators and small teams getting started.',
    bullets: [
      'Up to 3 team members',
      'Up to 50 bookings a month',
      { label: 'Email confirmations and reminders', href: 'https://bookaburra.com/features#reminders' },
      { label: 'Booking approval workflow', href: 'https://bookaburra.com/features#booking-approval' },
      { label: 'Booking page in 11 languages', href: 'https://bookaburra.com/features#languages' },
      { label: 'Bookings in Google, Apple, or Outlook Calendar', href: 'https://bookaburra.com/features#calendar-feed' },
      { label: 'Owner / manager / staff roles', href: 'https://bookaburra.com/features#roles' },
      { label: 'My day + daily 5pm summary', href: 'https://bookaburra.com/features#my-day' },
      { label: 'Inline help on every page', href: 'https://bookaburra.com/features#inline-help' },
      { label: 'Create bookings on behalf of clients', href: 'https://bookaburra.com/features#admin-create-booking' },
    ],
    notInTier: [
      'No SMS reminders or confirmations',
      'No waiting list when slots run out',
      'No booking chatbot or AI voice agent',
      'No sick-day booking reshuffle',
      'Cannot customise email wording (uses Bookaburra defaults)',
    ],
    cta: { label: 'Sign up free', href: signupUrl({ plan: 'free' }) },
    featured: false,
  },
  {
    name: 'Small business',
    price: 'A$29',
    cadence: '/month',
    headline:
      'For busier teams that need text reminders and a smarter front door.',
    bullets: [
      'Up to 20 team members',
      'Up to 300 bookings a month',
      'Everything in Free',
      { label: 'SMS booking confirmation + 24h reminder — 2 types only', href: 'https://bookaburra.com/notifications' },
      { label: 'Up to 200 SMS / month included (buy more credits any time)', href: 'https://bookaburra.com/notifications#sms' },
      { label: 'Booking chatbot that books and reschedules', href: 'https://bookaburra.com/features#booking-assistant' },
      'AI voice agent via booking site',
      { label: 'Internal-only services', href: 'https://bookaburra.com/features#service-config' },
      { label: 'Per-service business hours', href: 'https://bookaburra.com/features#service-config' },
    ],
    notInTier: [
      'SMS limited to booking confirmation + 24h reminder only (no other types)',
      'No waiting list when slots run out',
      'No sick-day booking reshuffle',
      'Cannot customise email or SMS wording (uses Bookaburra defaults)',
    ],
    cta: {
      label: 'Start Small business',
      href: signupUrl({ plan: 'small-business' }),
    },
    featured: true,
  },
  {
    name: 'Large business',
    price: 'A$159',
    cadence: '/month',
    headline:
      'For larger teams that need full SMS control, waiting lists, and sick-day resilience.',
    bullets: [
      'Up to 100 team members',
      'Up to 2,000 bookings a month',
      'Everything in Small business',
      { label: 'All 12 SMS notification types — fully customisable', href: 'https://bookaburra.com/notifications#sms' },
      { label: 'Up to 1,000 SMS / month included (buy more credits any time)', href: 'https://bookaburra.com/notifications#sms' },
      { label: 'Enable/disable any SMS or email notification', href: 'https://bookaburra.com/features#notification-settings' },
      { label: 'Waiting list when slots run out', href: 'https://bookaburra.com/features#waiting-list' },
      { label: 'Sick-day booking reshuffle', href: 'https://bookaburra.com/features#sick-day-reshuffle' },
      { label: 'Customise email + SMS wording for your business', href: 'https://bookaburra.com/features#customise-wording' },
    ],
    notInTier: [],
    cta: {
      label: 'Start Large business',
      href: signupUrl({ plan: 'large-business' }),
    },
    featured: false,
  },
];

const compareRows: {
  label: string;
  /** Optional deep-link to the matching feature card. Opens in the
   *  shared `features` named tab (see render below). */
  href?: string;
  free: string | boolean;
  small: string | boolean;
  large: string | boolean;
}[] = [
  { label: 'Team members', free: 'Up to 3', small: 'Up to 20', large: 'Up to 100' },
  { label: 'Bookings per month', free: 'Up to 50', small: 'Up to 300', large: 'Up to 2,000' },
  {
    label: 'Bookable business address',
    free: '1 per account (unlimited virtual & on-site services)',
    small: '1 per account (unlimited virtual & on-site services)',
    large: '1 per account (unlimited virtual & on-site services)',
  },
  { label: 'Email confirmations and reminders', href: 'https://bookaburra.com/features#reminders', free: true, small: true, large: true },
  {
    label: 'SMS notifications',
    href: 'https://bookaburra.com/notifications#sms',
    free: 'None',
    small: 'Confirmation + 24h reminder only (200/month)',
    large: 'All 12 types, customisable (1,000/month)',
  },
  {
    label: 'Buy SMS top-up credits',
    href: 'https://bookaburra.com/notifications#sms',
    free: false,
    small: true,
    large: true,
  },
  { label: 'Booking approval workflow', href: 'https://bookaburra.com/features#booking-approval', free: true, small: true, large: true },
  { label: 'Waiting list when slots run out', href: 'https://bookaburra.com/features#waiting-list', free: false, small: false, large: true },
  { label: 'Booking chatbot', href: 'https://bookaburra.com/features#booking-assistant', free: false, small: true, large: true },
  { label: 'AI voice agent via booking site', free: false, small: true, large: true },
  { label: 'Enable/disable any notification channel', href: 'https://bookaburra.com/features#notification-settings', free: false, small: false, large: true },
  { label: 'Customise email + SMS wording', href: 'https://bookaburra.com/features#customise-wording', free: false, small: false, large: true },
  { label: 'Booking page in 11 languages', href: 'https://bookaburra.com/features#languages', free: true, small: true, large: true },
  { label: 'Sick-day booking reshuffle', href: 'https://bookaburra.com/features#sick-day-reshuffle', free: false, small: false, large: true },
  { label: 'Bookings in Google, Apple, or Outlook Calendar', href: 'https://bookaburra.com/features#calendar-feed', free: true, small: true, large: true },
  { label: 'Owner / manager / staff roles', href: 'https://bookaburra.com/features#roles', free: true, small: true, large: true },
  { label: 'My day + daily 5pm summary', href: 'https://bookaburra.com/features#my-day', free: true, small: true, large: true },
  { label: 'Inline help on every page', href: 'https://bookaburra.com/features#inline-help', free: true, small: true, large: true },
  { label: 'Create bookings on behalf of clients', href: 'https://bookaburra.com/features#admin-create-booking', free: true, small: true, large: true },
  { label: 'Internal-only services + per-service hours', href: 'https://bookaburra.com/features#service-config', free: false, small: true, large: true },
];

function PricingPage() {
  usePageTitle('Pricing');

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-16 pt-14 sm:px-12 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            Pricing
          </div>
          <h1 className="mt-6 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl">
            Save on admin costs, maximise bookings.
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-brand-ink-soft/80 sm:text-xl">
            {PAID_PLANS_LIVE ? (
              <>
                Start free. Upgrade only when you need text reminders, a
                booking chatbot, or an AI voice agent. No card required.
              </>
            ) : (
              <>
                Free is the only plan open right now. Paid plans with SMS,
                the booking chatbot, and the AI voice agent are coming soon.
              </>
            )}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild size="lg">
              <a href={signupUrl({ plan: 'free' })}>Start for free</a>
            </Button>
            <p className="text-sm text-brand-ink-soft/60">
              No card required.
            </p>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="bg-brand-cream-shadow">
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-12 sm:py-20">
          <div
            className={`grid gap-6 ${
              PAID_PLANS_LIVE ? 'lg:grid-cols-3' : 'lg:grid-cols-2'
            }`}
          >
            {(PAID_PLANS_LIVE ? tiers : tiers.filter((t) => t.name === 'Free')).map((tier) => (
              <article
                key={tier.name}
                className={`flex flex-col rounded-2xl border p-8 ${
                  tier.featured
                    ? 'border-brand-sage bg-brand-cream shadow-[0_30px_80px_-30px_rgba(95,127,102,0.35)] ring-1 ring-brand-sage/30'
                    : 'border-brand-sage/15 bg-brand-cream'
                }`}
              >
                <header className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-widest text-brand-sage">
                    {tier.name}
                  </h2>
                  {tier.featured ? (
                    <span className="rounded-full bg-brand-sage px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-cream">
                      Most teams
                    </span>
                  ) : null}
                </header>
                <p className="mt-6 flex items-baseline gap-1">
                  <span className="text-5xl font-extrabold tracking-tightest text-brand-ink-soft">
                    {tier.price}
                  </span>
                  {tier.cadence ? (
                    <span className="text-sm font-medium text-brand-ink-soft/55">
                      {tier.cadence}
                    </span>
                  ) : null}
                </p>
                <p className="mt-4 text-sm text-brand-ink-soft/75">
                  {tier.headline}
                </p>

                <ul className="mt-8 space-y-3">
                  {tier.bullets.map((b) => {
                    const label = typeof b === 'string' ? b : b.label;
                    const href = typeof b === 'string' ? undefined : b.href;
                    return (
                      <li key={label} className="flex items-start gap-2.5 text-sm text-brand-ink-soft">
                        <span aria-hidden className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-sage/15 text-[10px] text-brand-sage">&#10003;</span>
                        {href ? (
                          // Named target = same tab reused across every
                          // pricing-page click, so the visitor doesn't end
                          // up with a stack of feature tabs.
                          <a href={href} target="features" rel="noopener noreferrer" className="underline-offset-2 hover:underline hover:text-brand-sage transition-colors">{label}</a>
                        ) : (
                          <span>{label}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>

                {tier.notInTier.length > 0 ? (
                  <div className="mt-6 rounded-xl border border-brand-sage/15 bg-brand-cream-shadow/60 p-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-brand-ink-soft/55">
                      Not on Free
                    </p>
                    <ul className="mt-3 space-y-2">
                      {tier.notInTier.map((n) => (
                        <li
                          key={n}
                          className="flex items-start gap-2.5 text-sm text-brand-ink-soft/70"
                        >
                          <span
                            aria-hidden
                            className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-ink-soft/10 text-[10px] text-brand-ink-soft/55"
                          >
                            &mdash;
                          </span>
                          <span>{n}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div className="flex-1" />

                <Button
                  asChild
                  variant={tier.featured ? 'primary' : 'outline'}
                  size="lg"
                  className="mt-8"
                >
                  <a href={tier.cta.href}>{tier.cta.label}</a>
                </Button>
              </article>
            ))}

            {!PAID_PLANS_LIVE ? (
              <article className="flex flex-col rounded-2xl border border-dashed border-brand-sage/40 bg-brand-cream/40 p-8">
                <header className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold uppercase tracking-widest text-brand-sage">
                    Paid plans
                  </h2>
                  <span className="rounded-full bg-brand-sage/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-brand-sage">
                    Coming soon
                  </span>
                </header>
                <p className="mt-6 text-2xl font-semibold text-brand-ink-soft">
                  Small business + Large business
                </p>
                <p className="mt-4 text-sm text-brand-ink-soft/75">
                  Pricing is set; billing isn&rsquo;t wired up yet. The paid
                  plans add SMS reminders, the booking chatbot, the AI voice
                  agent, and the sick-day reshuffle.
                </p>
                <p className="mt-6 text-sm text-brand-ink-soft/55">
                  Start on Free now and you&rsquo;ll be first in line when
                  paid plans open.
                </p>
                <div className="mt-auto pt-8 text-xs text-brand-ink-soft/55">
                  Want a heads-up when they go live?{' '}
                  <a href="/contact" className="text-brand-sage underline-offset-4 hover:underline">
                    Drop us a line.
                  </a>
                </div>
              </article>
            ) : null}
          </div>
        </div>
      </section>

      {/* COMPARISON — always rendered. When paid plans aren't live we still
          show the table so people on Free can see what they'd be missing. */}
      <Section>
        <div className="max-w-3xl">
          <SectionEyebrow>What&rsquo;s in each plan</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            {PAID_PLANS_LIVE
              ? 'Pick the plan that fits your team.'
              : 'Find the right plan for your business'}
          </h2>
          {!PAID_PLANS_LIVE ? (
            <p className="mt-4 text-base text-brand-ink-soft/70">
              Small business and Large business plans are coming soon. The
              comparison below is the real shape of each plan — start on
              Free now and upgrade when they open.
            </p>
          ) : null}
        </div>
        <div className="mt-10 overflow-hidden rounded-2xl border border-brand-sage/15 bg-brand-cream">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-cream-shadow text-xs font-semibold uppercase tracking-widest text-brand-ink-soft/55">
              <tr>
                <th className="px-6 py-4">What you get</th>
                <th className="px-6 py-4 text-center">Free</th>
                <th className="px-6 py-4 text-center text-brand-sage">
                  Small business
                  {!PAID_PLANS_LIVE ? (
                    <span className="ml-1 block text-[9px] font-bold text-brand-ink-soft/40">
                      Coming soon
                    </span>
                  ) : null}
                </th>
                <th className="px-6 py-4 text-center">
                  Large business
                  {!PAID_PLANS_LIVE ? (
                    <span className="ml-1 block text-[9px] font-bold text-brand-ink-soft/40">
                      Coming soon
                    </span>
                  ) : null}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-sage/10">
              {compareRows.map((row) => (
                <tr key={row.label}>
                  <th className="px-6 py-4 font-medium text-brand-ink-soft" scope="row">
                    {row.href ? (
                      <a
                        href={row.href}
                        target="features"
                        rel="noopener noreferrer"
                        className="underline-offset-2 hover:underline hover:text-brand-sage transition-colors"
                      >
                        {row.label}
                      </a>
                    ) : (
                      row.label
                    )}
                  </th>
                  <CompareCell value={row.free} />
                  <CompareCell value={row.small} highlight />
                  <CompareCell value={row.large} />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* FAQ */}
      <Section tone="shadow">
        <div className="max-w-3xl">
          <SectionEyebrow>Before you sign up</SectionEyebrow>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-brand-ink-soft sm:text-5xl">
            {PAID_PLANS_LIVE
              ? 'Remove the usual pricing doubts.'
              : 'A few things worth knowing.'}
          </h2>
        </div>
        <dl className="mt-12 grid gap-10 sm:grid-cols-2">
          <FaqItem
            q="Is Free really free?"
            a="Yes. Up to 3 team members and 50 bookings a month, no card needed. Upgrade only if you need SMS reminders, the booking chatbot, or the AI voice agent."
          />
          <FaqItem
            q="What happens if I hit my monthly booking limit?"
            a="Your booking page stays live and existing bookings are never affected. New bookings pause until the next month, or you can upgrade and they pick straight back up."
          />
          <FaqItem
            q="How many business addresses does each account cover?"
            a="One bookable street address per account — every plan, including Large business. From that base you can offer unlimited services delivered at your premises, at the client&rsquo;s address, by video call, or over the phone. Multi-site businesses can use multiple Bookaburra accounts (one login covers all of them), but each account has a single address."
          />
          <FaqItem
            q="What counts as a team member?"
            a="Anyone who needs their own login and calendar — staff, casual staff, receptionists, and managers. The owner counts too."
          />
          <FaqItem
            q="Where is my data hosted?"
            a="In Australia."
          />
          {PAID_PLANS_LIVE ? (
            <>
              <FaqItem
                q="Can I switch plans later?"
                a="Upgrade any time — it takes effect immediately and your card is charged only the prorated difference for the rest of the current period. Downgrade any time too — unused days are refunded immediately and your plan drops straight away. Your bookings come with you either way."
              />
              <FaqItem
                q="What happens if I cancel?"
                a="Cancel any time. Unused days on your current billing period are refunded immediately and your account drops to Free. Your booking history stays intact."
              />
            </>
          ) : (
            <FaqItem
              q="When will paid plans be available?"
              a="No firm date. Bookaburra is being built as a side project; paid plans need a registered company and billing set up before they can open. The Free plan is the only one you can sign up for today."
            />
          )}
        </dl>
      </Section>

      {/* CLOSER CTA */}
      <Section tone="ink">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-brand-cream sm:text-4xl">
            Ready to take the first booking?
          </h2>
          <p className="mt-4 text-lg text-brand-cream/75">
            The Free plan covers up to 3 team members and 50 bookings a month.
            Set up takes about ten minutes.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button asChild size="lg" variant="primary">
              <a href={signupUrl({ plan: 'free' })}>Start for free</a>
            </Button>
            <Button asChild size="lg" variant="outline">
              <a href="/features">See what&rsquo;s included</a>
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}

function CompareCell({
  value,
  highlight,
}: {
  value: string | boolean;
  highlight?: boolean;
}) {
  const bg = highlight ? 'bg-brand-cream-shadow' : '';
  if (typeof value === 'boolean') {
    return (
      <td className={`px-6 py-4 text-center ${bg}`}>
        {value ? (
          <span aria-label="Included" className="text-brand-sage">
            &#10003;
          </span>
        ) : (
          <span aria-label="Not included" className="text-brand-ink-soft/25">
            &mdash;
          </span>
        )}
      </td>
    );
  }
  return (
    <td className={`px-6 py-4 text-center text-brand-ink-soft/80 ${bg}`}>
      {value}
    </td>
  );
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div>
      <dt className="text-lg font-semibold text-brand-ink-soft">{q}</dt>
      <dd className="mt-3 text-base text-brand-ink-soft/75">{a}</dd>
    </div>
  );
}
